import { useEffect, useRef, useState } from 'react';

export default function CitationIntegrity() {
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setAnimated(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const points = [
    {
      icon: '🔍',
      heading: 'Every citation gets checked',
      body: 'Before you see a final document, every citation is checked. Case opinions against a database of over 10 million court decisions. Statutory and code citations against current state and federal code. If it does not exist, you will know.',
    },
    {
      icon: '✏️',
      heading: 'You review before it finalizes',
      body: 'Before a document is generated, HushLegal surfaces all proper nouns, party names, and case references for your review. Nothing is locked in without your sign-off.',
    },
    {
      icon: '🔒',
      heading: 'No export until citations clear',
      body: 'You cannot download or export a document with unverified citations. Each one must either pass verification or be explicitly acknowledged by you first.',
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-20 sm:py-32 bg-[#09090b]">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8">

        <div className={`text-center mb-12 sm:mb-16 transition-all duration-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-[#06b6d4] text-xs font-semibold tracking-widest uppercase mb-4">
            Citation integrity
          </p>
          <h2 className="text-white text-[28px] sm:text-[40px] lg:text-[52px] font-bold leading-tight mb-5">
            No hallucinated citations
          </h2>
          <p className="text-[#a1a1aa] text-base sm:text-lg font-light max-w-[620px] mx-auto leading-relaxed">
            Lawyers have been sanctioned for filing AI-generated briefs with cases that do not exist. We built HushLegal so that cannot happen on our watch. That includes case opinions, statutes, and code citations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-10 sm:mb-14">
          {points.map((point, index) => (
            <div
              key={index}
              className="p-6 sm:p-8 bg-[#0f0f11] border border-[#1a1a1c] rounded-2xl hover:border-[#06b6d4]/40 transition-all duration-500"
              style={{
                transitionDelay: `${index * 150}ms`,
                opacity: animated ? 1 : 0,
                transform: animated ? 'translateY(0)' : 'translateY(24px)',
              }}
            >
              <div className="text-3xl mb-4">{point.icon}</div>
              <h3 className="text-white text-base sm:text-lg font-semibold mb-3">{point.heading}</h3>
              <p className="text-[#a1a1aa] text-[14px] sm:text-[15px] font-light leading-relaxed">{point.body}</p>
            </div>
          ))}
        </div>

        <div
          className={`max-w-[780px] mx-auto text-center transition-all duration-700 delay-500 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-white text-lg sm:text-xl font-medium leading-relaxed">
            Nothing goes out the door with a citation we cannot verify.
          </p>
          <p className="text-[#71717a] text-sm mt-3 font-light">
            That is the whole promise. We are not going to let you get sanctioned on our account.
          </p>
        </div>

      </div>
    </section>
  );
}
